import React, { useState, useEffect } from "react";
import Sidebar from "../../components/Admin/Sidebar";
import Header from "../../components/Admin/Header";
import api from "../../api/axois";
import styles from "./KitchenDashboard.module.css";
import {
  FaUtensils, FaClipboardList, FaClock, FaPlus, FaTrash,
  FaEdit, FaToggleOn, FaToggleOff, FaSearch
} from "react-icons/fa";
import { getImageUrl } from "../../utils/imageUtils";
import { formatFileSize } from "../../utils/imageCompression";
import ImageCropModal from "../../components/Kitchen/ImageCropModal";
import { useSearchParams } from "react-router-dom";
import { matchesSearch, usePagination } from "../../utils/usePagination";
import PaginationBar from "../../components/Admin/PaginationBar";

const FALLBACK_ITEM_IMAGE = "https://images.unsplash.com/photo-1546069901-ba9599a7e63c";

// The shared food catalogue is sold on two storefronts, each with its own
// price/listing per item and listing/order per category.
const STOREFRONTS = [
  { key: "aurelia", label: "Aurelia" },
  { key: "chopnation", label: "Chop Nation" },
];

// The menu tab a category sits under — mirrors FoodCategory::GROUPS.
const CATEGORY_GROUPS = [
  { value: "meals", label: "Rice & Pasta" },
  { value: "swallow", label: "Swallow & Soup" },
  { value: "sides", label: "Proteins & Sides" },
  { value: "small_chops", label: "Small Chops" },
  { value: "drinks", label: "Drinks" },
  { value: "combos", label: "Combos" },
  { value: "party_packs", label: "Party Packs" },
];

const storefrontRow = (record, storefront) =>
  (record?.storefronts || []).find(row => row.storefront === storefront);

// Every add-on has one price per storefront.
const formatAddonPrices = (addon, storefront) => {
  const price = storefrontRow(addon, storefront)?.price ?? addon.price;
  return `₦${parseFloat(price ?? 0).toLocaleString()}`;
};

const KITCHEN_TABS = ["orders", "menu", "addons", "choices", "delivery"];

const ORDER_STATUS_FILTERS = [
  { value: "all", label: "All active" },
  { value: "pending", label: "Pending" },
  { value: "processing", label: "Preparing" },
  { value: "prepared", label: "Ready" },
];

/** A labelled <select> filter; highlighted when it isn't on its first ("all") option. */
const FilterSelect = ({ label, value, options, onChange }) => (
  <select
    aria-label={label}
    value={value}
    onChange={e => onChange(e.target.value)}
    className={`${styles.filterSelect} ${value !== options[0].value ? styles.filterSelectActive : ""}`}
  >
    {options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
  </select>
);

/** "12 of 40 items · Clear filters". */
const FilterSummary = ({ shown, total, noun, onClear }) => (
  <p className={styles.filterSummary}>
    {shown === total ? `${total} ${noun}` : `${shown} of ${total} ${noun}`}
    {onClear && (
      <>
        {" · "}
        <button type="button" onClick={onClear} className={styles.clearFilters}>Clear filters</button>
      </>
    )}
  </p>
);

/** Minutes since an order was placed, coloured once it's getting late. */
const waitingLabel = (createdAt) => {
  const minutes = Math.max(0, Math.floor((Date.now() - Date.parse(createdAt)) / 60000));
  const text = minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
  const color = minutes >= 30 ? "#c62828" : minutes >= 15 ? "#e65100" : "#666";
  return { text, color };
};

const PRICE_MODE_LABELS = {
  standard: "Normal price",
  included: "Included (free)",
  upcharge: "Upcharge",
};

const groupRuleSummary = (group) => {
  const pick = group.selection_type === "single"
    ? "Pick 1"
    : group.max_select
      ? `Pick ${group.min_select || 0}–${group.max_select}`
      : group.min_select ? `Pick ${group.min_select}+` : "Pick any";
  return `${pick} · ${group.is_required ? "Required" : "Optional"}`;
};

const emptyGroupForm = () => ({
  name: "",
  selection_type: "single",
  is_required: true,
  min_select: "1",
  max_select: "",
});

const emptyItemForm = () => ({
  name: "",
  category_id: "",
  prices: { aurelia: "", chopnation: "" },
  listed: { aurelia: true, chopnation: true },
  // Optional "was" price per storefront, shown struck through for deals.
  compareAt: { aurelia: "", chopnation: "" },
  // The item's own choice groups, in order (a combo's steps) — empty means
  // it uses its category's groups.
  groupIds: [],
  description: "",
  is_available: true,
  ingredients: "",
  image: null
});

const KitchenDashboard = () => {
  // The tab lives in the URL (?tab=menu), so a refresh or a shared link opens
  // the same tab instead of jumping back to Orders.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = KITCHEN_TABS.includes(searchParams.get("tab")) ? searchParams.get("tab") : "orders";
  const setActiveTab = (tab) => setSearchParams(tab === "orders" ? {} : { tab }, { replace: true });
  const [orders, setOrders] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [addonGroups, setAddonGroups] = useState([]);
  const [deliveryLocations, setDeliveryLocations] = useState([]);
  const [newLocationLabel, setNewLocationLabel] = useState("");
  const [newLocationFee, setNewLocationFee] = useState("");
  const [editingLocationId, setEditingLocationId] = useState(null);
  const [editingLocationFee, setEditingLocationFee] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // A photo just picked for a menu item, while it's in the cropper.
  const [pickedPhoto, setPickedPhoto] = useState(null);
  
  // Modal states
  const [showItemModal, setShowItemModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [itemForm, setItemForm] = useState(emptyItemForm);
  const [categoryOrderStorefront, setCategoryOrderStorefront] = useState("aurelia");
  const [addons, setAddons] = useState([]);
  const [editingAddon, setEditingAddon] = useState(null);
  // { [storefront]: { is_listed, price } }
  const [addonForm, setAddonForm] = useState(null);
  const [savingAddon, setSavingAddon] = useState(false);
  // Choice groups: null = closed, {} = creating, group = editing rules.
  const [groupRulesTarget, setGroupRulesTarget] = useState(null);
  const [groupForm, setGroupForm] = useState(emptyGroupForm);
  // { group, members: { [addonId]: { included, mode, upcharges: { [storefront]: "" }, order } } }
  const [groupMembersEdit, setGroupMembersEdit] = useState(null);
  const [savingGroup, setSavingGroup] = useState(false);
  const [categoryBusyId, setCategoryBusyId] = useState(null);
  const [menuSearchQuery, setMenuSearchQuery] = useState("");
  const [menuCategoryFilter, setMenuCategoryFilter] = useState("all");

  // Which add-on groups (Protein Add-ons, Soup, Spice Level, ...) apply to a
  // category — every item in that category inherits this set, there's no
  // per-item override (see MenuController::addonGroupsForItem on the backend).
  const [showCategoryAddonsModal, setShowCategoryAddonsModal] = useState(false);
  const [editingCategoryForAddons, setEditingCategoryForAddons] = useState(null);
  const [selectedAddonGroupIds, setSelectedAddonGroupIds] = useState([]);
  const [savingCategoryAddons, setSavingCategoryAddons] = useState(false);

  // --- List filters & pagination --------------------------------------------
  const [menuSiteFilter, setMenuSiteFilter] = useState("all");
  const [menuStockFilter, setMenuStockFilter] = useState("all");

  const filteredMenuItems = menuItems.filter(item => {
    const listedOn = (key) => storefrontRow(item, key)?.is_listed ?? true;
    const matchesSite =
      menuSiteFilter === "all" ||
      (menuSiteFilter === "hidden_both" ? STOREFRONTS.every(({ key }) => !listedOn(key))
        : menuSiteFilter.startsWith("only_") ? STOREFRONTS.every(({ key }) => listedOn(key) === (key === menuSiteFilter.slice(5)))
        : listedOn(menuSiteFilter));
    return (
      (menuCategoryFilter === "all" || String(item.category_id) === String(menuCategoryFilter)) &&
      matchesSearch(menuSearchQuery, item.name, item.description) &&
      matchesSite &&
      (menuStockFilter === "all" || (menuStockFilter === "in_stock") === item.is_available)
    );
  });
  const menuFiltersActive = menuSearchQuery !== "" || menuCategoryFilter !== "all" || menuSiteFilter !== "all" || menuStockFilter !== "all";
  const menuPagination = usePagination(
    filteredMenuItems, 20, `${menuSearchQuery}|${menuCategoryFilter}|${menuSiteFilter}|${menuStockFilter}`
  );

  const [orderSearch, setOrderSearch] = useState("");
  const [orderStatusFilter, setOrderStatusFilter] = useState("all");
  const [orderSiteFilter, setOrderSiteFilter] = useState("all");
  const [orderSort, setOrderSort] = useState("oldest");
  const ordersMatchingSearch = orders.filter(order =>
    (orderSiteFilter === "all" || order.storefront === orderSiteFilter) &&
    matchesSearch(orderSearch, order.id, order.customer_name, order.customer_phone, order.room_number, order.location,
      ...(order.items || []).map(item => item.item_name))
  );
  const filteredOrders = ordersMatchingSearch
    .filter(order => orderStatusFilter === "all" || order.status === orderStatusFilter)
    .sort((a, b) => (orderSort === "oldest" ? 1 : -1) * (Date.parse(a.created_at) - Date.parse(b.created_at)));
  const ordersFiltersActive = orderSearch !== "" || orderStatusFilter !== "all" || orderSiteFilter !== "all";
  const ordersPagination = usePagination(
    filteredOrders, 10, `${orderSearch}|${orderStatusFilter}|${orderSiteFilter}|${orderSort}`
  );

  const [addonSearch, setAddonSearch] = useState("");
  const [addonTypeFilter, setAddonTypeFilter] = useState("all");
  const [addonGroupFilter, setAddonGroupFilter] = useState("all");
  const [addonStockFilter, setAddonStockFilter] = useState("all");
  const filteredAddons = addons.filter(addon =>
    matchesSearch(addonSearch, addon.name) &&
    (addonTypeFilter === "all" || addon.type === addonTypeFilter) &&
    (addonGroupFilter === "all" || String(addon.group?.id ?? "none") === addonGroupFilter) &&
    (addonStockFilter === "all" || (addonStockFilter === "in_stock") === addon.is_available)
  );
  const addonFiltersActive = addonSearch !== "" || addonTypeFilter !== "all" || addonGroupFilter !== "all" || addonStockFilter !== "all";
  const addonsPagination = usePagination(
    filteredAddons, 20, `${addonSearch}|${addonTypeFilter}|${addonGroupFilter}|${addonStockFilter}`
  );

  const [groupSearch, setGroupSearch] = useState("");
  const [groupUsageFilter, setGroupUsageFilter] = useState("all");
  const filteredGroups = addonGroups.filter(group =>
    matchesSearch(groupSearch, group.name, ...(group.addons || []).map(addon => addon.name)) &&
    (groupUsageFilter === "all" ||
      (groupUsageFilter === "required" && group.is_required) ||
      (groupUsageFilter === "optional" && !group.is_required) ||
      (groupUsageFilter === "unused" && (group.menu_items_count || 0) + (group.categories_count || 0) === 0))
  );
  const groupsPagination = usePagination(filteredGroups, 10, `${groupSearch}|${groupUsageFilter}`);

  const [locationSearch, setLocationSearch] = useState("");
  const [locationStatusFilter, setLocationStatusFilter] = useState("all");
  const filteredLocations = deliveryLocations.filter(location =>
    matchesSearch(locationSearch, location.label) &&
    (locationStatusFilter === "all" || (locationStatusFilter === "active") === location.is_active)
  );
  const locationsPagination = usePagination(filteredLocations, 20, `${locationSearch}|${locationStatusFilter}`);

  const fetchData = async () => {
    try {
      setError(null);
      const token = localStorage.getItem('token');
      const user = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : null;
      
      console.log('Kitchen Dashboard - Token present:', !!token);
      console.log('Kitchen Dashboard - User role:', user?.role);
      console.log('Kitchen Dashboard - Full user:', user);
      
      // First, check auth status
      console.log('Checking auth status...');
      const debugRes = await api.get("/chopnation/auth/debug");
      console.log('Auth debug response:', debugRes.data);
      
      const [ordersRes, menuRes, catsRes, deliveryRes, addonGroupsRes, addonsRes] = await Promise.all([
        api.get("/chopnation/kitchen/dashboard"),
        api.get("/chopnation/kitchen/menu-items"),
        api.get("/chopnation/kitchen/categories"),
        api.get("/chopnation/kitchen/delivery-locations"),
        api.get("/chopnation/kitchen/addon-groups"),
        api.get("/chopnation/kitchen/addons")
      ]);

      setOrders(ordersRes.data);
      setMenuItems(menuRes.data);
      setCategories(catsRes.data);
      setDeliveryLocations(deliveryRes.data);
      setAddonGroups(addonGroupsRes.data);
      setAddons(addonsRes.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching kitchen data:", error);
      console.error("Error status:", error.response?.status);
      console.error("Error data:", error.response?.data);
      
      if (error.response?.status === 401 || error.response?.status === 403) {
        const user = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : null;
        if (user?.role !== 'kitchen_staff') {
          setError(`Access denied. Your role is '${user?.role || 'unknown'}' but kitchen staff is required.`);
        } else {
          setError("Unauthorized. Please log in to access the kitchen dashboard.");
        }
      } else {
        setError(error.response?.data?.message || "Failed to load kitchen data. Please try again.");
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => {
        if (activeTab === "orders") fetchData();
    }, 20000);
    return () => clearInterval(interval);
  }, [activeTab]);

  const handleUpdateStatus = async (orderId, status) => {
    try {
      await api.patch(`/chopnation/kitchen/orders/${orderId}/status`, { status });
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status } : o));
    } catch (err) {
      alert("Error updating order");
    }
  };

  const buildMenuItemFormData = () => {
    const formData = new FormData();
    formData.append("category_id", itemForm.category_id);
    formData.append("name", itemForm.name);
    formData.append("description", itemForm.description);
    // base_price is the default for storefronts added later; each
    // storefront's actual price/listing goes in storefronts[...].
    formData.append("base_price", itemForm.prices.aurelia);
    formData.append("is_available", itemForm.is_available ? "1" : "0");
    STOREFRONTS.forEach(({ key }) => {
        formData.append(`storefronts[${key}][price]`, itemForm.prices[key]);
        formData.append(`storefronts[${key}][is_listed]`, itemForm.listed[key] ? "1" : "0");
        // Only a "was" price above the price makes sense; anything else clears it.
        const compareAt = parseFloat(itemForm.compareAt[key]);
        formData.append(
            `storefronts[${key}][compare_at_price]`,
            compareAt > parseFloat(itemForm.prices[key]) ? String(compareAt) : ""
        );
    });
    // Form data can't carry an empty array — an empty value means "use the category's".
    if (itemForm.groupIds.length === 0) {
        formData.append("addon_group_ids", "");
    }
    itemForm.groupIds.forEach(id => formData.append("addon_group_ids[]", id));

    const ingredientsArray = itemForm.ingredients
        ? itemForm.ingredients.split(',').map(s => s.trim()).filter(Boolean)
        : [];
    formData.append("ingredients", JSON.stringify(ingredientsArray));

    // Only send 'image' when a new file was picked. The backend leaves the
    // existing image untouched when it's absent, so there's no need to
    // resend the current path as a string on every save.
    if (itemForm.image instanceof File) {
        formData.append("image", itemForm.image);
    }

    return formData;
  };

  const resetItemForm = () => {
    setShowItemModal(false);
    setEditingItem(null);
    setItemForm(emptyItemForm());
  };

  const handleSaveItem = async (e) => {
    e.preventDefault();
    try {
        const formData = buildMenuItemFormData();

        if (editingItem) {
            formData.append("_method", "PATCH");
            // Don't set Content-Type manually here — the browser needs to add its
            // own multipart boundary, which an explicit header value overwrites,
            // breaking the upload silently (fields parse as empty on the backend).
            await api.post(`/chopnation/kitchen/menu-items/${editingItem.id}`, formData);
        } else {
            await api.post("/chopnation/kitchen/menu-items", formData);
        }
        fetchData();
        resetItemForm();
    } catch (err) {
        const errors = err.response?.data?.errors;
        const message = errors
            ? Object.values(errors).flat().join(' ')
            : err.response?.data?.message || "Error saving menu item";
        alert(message);
    }
  };

  const handleDeleteItem = async (id) => {
    if (!window.confirm("Are you sure?")) return;
    try {
        await api.delete(`/chopnation/kitchen/menu-items/${id}`);
        setMenuItems(prev => prev.filter(i => i.id !== id));
    } catch (err) {
        alert("Error deleting item");
    }
  };

  const toggleItemAvailability = async (itemId) => {
    try {
      const res = await api.post(`/chopnation/kitchen/items/${itemId}/toggle`);
      setMenuItems(prev => prev.map(item => 
        item.id === itemId ? { ...item, is_available: res.data.is_available } : item
      ));
    } catch (error) {
       alert("Failed to toggle item");
    }
  };

  const openCategoryAddonsModal = (category) => {
    setEditingCategoryForAddons(category);
    setSelectedAddonGroupIds((category.addonGroups || []).map(g => g.id));
    setShowCategoryAddonsModal(true);
  };

  const toggleSelectedAddonGroup = (groupId) => {
    setSelectedAddonGroupIds(prev =>
      prev.includes(groupId) ? prev.filter(id => id !== groupId) : [...prev, groupId]
    );
  };

  const handleSaveCategoryAddonGroups = async (e) => {
    e.preventDefault();
    setSavingCategoryAddons(true);
    try {
      const res = await api.patch(`/chopnation/kitchen/categories/${editingCategoryForAddons.id}/addon-groups`, {
        addon_group_ids: selectedAddonGroupIds
      });
      setCategories(prev => prev.map(c => c.id === res.data.category.id ? { ...c, ...res.data.category } : c));
      setShowCategoryAddonsModal(false);
      setEditingCategoryForAddons(null);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to update add-on groups");
    } finally {
      setSavingCategoryAddons(false);
    }
  };

  const openAddonModal = (addon) => {
    setEditingAddon(addon);
    setAddonForm(Object.fromEntries(STOREFRONTS.map(({ key }) => {
      const row = storefrontRow(addon, key);
      return [key, {
        is_listed: row?.is_listed ?? true,
        price: String(parseFloat(row?.price ?? addon.price ?? 0)),
      }];
    })));
  };

  const closeAddonModal = () => {
    setEditingAddon(null);
    setAddonForm(null);
  };

  const setAddonField = (storefront, field, value) =>
    setAddonForm(prev => ({ ...prev, [storefront]: { ...prev[storefront], [field]: value } }));

  const handleSaveAddon = async (e) => {
    e.preventDefault();
    const storefronts = Object.fromEntries(STOREFRONTS.map(({ key }) => [
      key,
      { is_listed: addonForm[key].is_listed, price: parseFloat(addonForm[key].price) },
    ]));

    setSavingAddon(true);
    try {
      const res = await api.patch(`/chopnation/kitchen/addons/${editingAddon.id}`, { storefronts });
      setAddons(prev => prev.map(a => a.id === editingAddon.id ? { ...a, ...res.data.addon } : a));
      closeAddonModal();
    } catch (err) {
      const errors = err.response?.data?.errors;
      alert(errors ? Object.values(errors).flat().join(' ') : err.response?.data?.message || "Failed to save add-on");
    } finally {
      setSavingAddon(false);
    }
  };

  const toggleAddonAvailability = async (addon) => {
    try {
      const res = await api.post(`/chopnation/kitchen/addons/${addon.id}/toggle`);
      setAddons(prev => prev.map(a => a.id === addon.id ? { ...a, is_available: res.data.is_available } : a));
    } catch (err) {
      alert(err.response?.data?.message || "Failed to toggle add-on");
    }
  };

  const openGroupRules = (group) => {
    setGroupRulesTarget(group || {});
    setGroupForm(group ? {
      name: group.name,
      selection_type: group.selection_type,
      is_required: group.is_required,
      min_select: String(group.min_select ?? 0),
      max_select: group.max_select ? String(group.max_select) : "",
    } : emptyGroupForm());
  };

  const refreshAddonGroups = async () => {
    const res = await api.get("/chopnation/kitchen/addon-groups");
    setAddonGroups(res.data);
  };

  const handleSaveGroupRules = async (e) => {
    e.preventDefault();
    const single = groupForm.selection_type === "single";
    const payload = {
      name: groupForm.name.trim(),
      selection_type: groupForm.selection_type,
      is_required: groupForm.is_required,
      // A single choice is always exactly one (or none, when optional).
      min_select: single ? (groupForm.is_required ? 1 : 0) : Math.max(0, parseInt(groupForm.min_select, 10) || 0),
      max_select: single ? 1 : (groupForm.max_select ? Math.max(1, parseInt(groupForm.max_select, 10)) : null),
    };

    setSavingGroup(true);
    try {
      if (groupRulesTarget.id) {
        await api.patch(`/chopnation/kitchen/addon-groups/${groupRulesTarget.id}`, payload);
      } else {
        await api.post("/chopnation/kitchen/addon-groups", payload);
      }
      await refreshAddonGroups();
      setGroupRulesTarget(null);
    } catch (err) {
      const errors = err.response?.data?.errors;
      alert(errors ? Object.values(errors).flat().join(' ') : err.response?.data?.message || "Failed to save group");
    } finally {
      setSavingGroup(false);
    }
  };

  const handleDeleteGroup = async (group) => {
    const usedBy = (group.menu_items_count || 0) + (group.categories_count || 0);
    if (!window.confirm(usedBy
      ? `"${group.name}" is used by ${usedBy} item(s)/categories. Delete it anyway?`
      : `Delete "${group.name}"?`)) return;
    try {
      await api.delete(`/chopnation/kitchen/addon-groups/${group.id}`);
      setAddonGroups(prev => prev.filter(g => g.id !== group.id));
    } catch (err) {
      alert(err.response?.data?.message || "Failed to delete group");
    }
  };

  const openGroupMembers = (group) => {
    const members = Object.fromEntries(addons.map(addon => {
      const member = (group.addons || []).find(a => a.id === addon.id);
      const upcharges = member?.pivot?.upcharges || {};
      return [addon.id, {
        included: !!member,
        mode: member?.pivot?.price_mode || "standard",
        upcharges: Object.fromEntries(STOREFRONTS.map(({ key }) => [key, upcharges[key] !== undefined ? String(upcharges[key]) : ""])),
        order: member ? member.pivot.sort_order : Number.MAX_SAFE_INTEGER,
      }];
    }));
    setGroupMembersEdit({ group, members });
  };

  const updateMember = (addonId, changes) =>
    setGroupMembersEdit(prev => ({
      ...prev,
      members: { ...prev.members, [addonId]: { ...prev.members[addonId], ...changes } },
    }));

  const orderedGroupAddons = () => {
    const { members } = groupMembersEdit;
    return [...addons].sort((a, b) => members[a.id].order - members[b.id].order || a.name.localeCompare(b.name));
  };

  const handleSaveGroupMembers = async () => {
    const { group, members } = groupMembersEdit;
    const payload = orderedGroupAddons()
      .filter(addon => members[addon.id].included)
      .map(addon => {
        const member = members[addon.id];
        return {
          addon_id: addon.id,
          price_mode: member.mode,
          upcharges: member.mode === "upcharge"
            ? Object.fromEntries(STOREFRONTS.map(({ key }) => [key, parseFloat(member.upcharges[key]) || 0]))
            : null,
        };
      });

    setSavingGroup(true);
    try {
      await api.put(`/chopnation/kitchen/addon-groups/${group.id}/addons`, { addons: payload });
      await refreshAddonGroups();
      setGroupMembersEdit(null);
    } catch (err) {
      const errors = err.response?.data?.errors;
      alert(errors ? Object.values(errors).flat().join(' ') : err.response?.data?.message || "Failed to save options");
    } finally {
      setSavingGroup(false);
    }
  };

  const describeGroupOption = (addon) => {
    const mode = addon.pivot?.price_mode;
    if (mode === "included") return `${addon.name} (included)`;
    if (mode === "upcharge") {
      const upcharges = addon.pivot.upcharges || {};
      return `${addon.name} (+${STOREFRONTS.map(({ key, label }) => `${label} ₦${parseFloat(upcharges[key] || 0).toLocaleString()}`).join(" / ")})`;
    }
    return addon.name;
  };

  const updateCategory = async (category, payload) => {
    setCategoryBusyId(category.id);
    try {
      const res = await api.patch(`/chopnation/kitchen/categories/${category.id}`, payload);
      setCategories(prev => prev.map(c => c.id === category.id ? { ...c, ...res.data.category } : c));
    } catch (err) {
      alert(err.response?.data?.message || "Failed to update category");
    } finally {
      setCategoryBusyId(null);
    }
  };

  const toggleCategoryListing = (category, storefront) =>
    updateCategory(category, {
      storefronts: { [storefront]: { is_listed: !(storefrontRow(category, storefront)?.is_listed ?? false) } },
    });

  const categoriesInOrder = (storefront) =>
    [...categories].sort((a, b) =>
      (storefrontRow(a, storefront)?.sort_order ?? Number.MAX_SAFE_INTEGER) -
        (storefrontRow(b, storefront)?.sort_order ?? Number.MAX_SAFE_INTEGER) || a.id - b.id
    );

  const moveCategory = async (index, direction) => {
    const ordered = categoriesInOrder(categoryOrderStorefront);
    const target = index + direction;
    if (target < 0 || target >= ordered.length) return;

    const ids = ordered.map(c => c.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    try {
      const res = await api.put('/chopnation/kitchen/categories/order', {
        storefront: categoryOrderStorefront,
        category_ids: ids,
      });
      setCategories(res.data.categories);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to reorder categories");
    }
  };

  const handleAddDeliveryLocation = async (e) => {
    e.preventDefault();
    if (!newLocationLabel.trim() || newLocationFee === "") return;
    try {
      const res = await api.post('/chopnation/kitchen/delivery-locations', {
        label: newLocationLabel.trim(),
        fee: parseFloat(newLocationFee),
      });
      setDeliveryLocations(prev => [...prev, res.data.location].sort((a, b) => a.label.localeCompare(b.label)));
      setNewLocationLabel("");
      setNewLocationFee("");
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to add delivery area');
    }
  };

  const handleToggleLocationActive = async (location) => {
    try {
      const res = await api.patch(`/chopnation/kitchen/delivery-locations/${location.id}`, {
        is_active: !location.is_active,
      });
      setDeliveryLocations(prev => prev.map(l => l.id === location.id ? res.data.location : l));
    } catch (err) {
      alert(err.response?.data?.message || "Failed to update delivery area");
    }
  };

  const startEditingLocationFee = (location) => {
    setEditingLocationId(location.id);
    setEditingLocationFee(String(location.fee));
  };

  const submitLocationFeeEdit = async (e, location) => {
    e.preventDefault();
    const fee = parseFloat(editingLocationFee);
    if (Number.isNaN(fee) || fee < 0) return;
    try {
      const res = await api.patch(`/chopnation/kitchen/delivery-locations/${location.id}`, { fee });
      setDeliveryLocations(prev => prev.map(l => l.id === location.id ? res.data.location : l));
      setEditingLocationId(null);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to update delivery fee");
    }
  };

  const handleDeleteDeliveryLocation = async (id) => {
    if (!window.confirm("Remove this delivery area?")) return;
    try {
      await api.delete(`/chopnation/kitchen/delivery-locations/${id}`);
      setDeliveryLocations(prev => prev.filter(l => l.id !== id));
    } catch (err) {
      alert(err.response?.data?.message || "Error removing delivery area");
    }
  };

  const renderDeliveryAreas = () => (
    <div className={styles.tabContent}>
      <div className={styles.gridHeader}>
        <h2>Delivery Areas</h2>
        <div className={styles.addCategoryGroup}>
          <input
            type="text"
            placeholder="Area name (e.g. Jabi)"
            value={newLocationLabel}
            onChange={e => setNewLocationLabel(e.target.value)}
            className={styles.categoryInput}
          />
          <input
            type="number"
            placeholder="Fee (₦)"
            value={newLocationFee}
            onChange={e => setNewLocationFee(e.target.value)}
            className={styles.categoryInput}
          />
          <button className={styles.addSmallBtn} onClick={handleAddDeliveryLocation}>Add Area</button>
        </div>
      </div>

      <div className={styles.filterRow}>
        <div className={styles.menuSearchBar}>
          <FaSearch className={styles.menuSearchIcon} />
          <input
            type="search"
            placeholder="Search areas..."
            value={locationSearch}
            onChange={e => setLocationSearch(e.target.value)}
            className={styles.menuSearchInput}
          />
        </div>
        <FilterSelect
          label="Status"
          value={locationStatusFilter}
          onChange={setLocationStatusFilter}
          options={[
            { value: "all", label: "Active & hidden" },
            { value: "active", label: "Active" },
            { value: "hidden", label: "Hidden" },
          ]}
        />
      </div>
      <FilterSummary
        shown={filteredLocations.length}
        total={deliveryLocations.length}
        noun="delivery areas"
        onClear={locationSearch || locationStatusFilter !== "all" ? () => { setLocationSearch(""); setLocationStatusFilter("all"); } : null}
      />

      <div className={styles.tableResponsive}>
        <table className={styles.itemTable}>
          <thead>
            <tr>
              <th>Area</th>
              <th>Delivery Fee</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredLocations.length === 0 ? (
              <tr><td colSpan={4} style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                {deliveryLocations.length === 0 ? "No delivery areas yet." : "No delivery areas match these filters."}
              </td></tr>
            ) : locationsPagination.pageItems.map(location => (
              <tr key={location.id}>
                <td>{location.label}</td>
                <td>
                  {editingLocationId === location.id ? (
                    <form onSubmit={(e) => submitLocationFeeEdit(e, location)}>
                      <input
                        type="number"
                        autoFocus
                        value={editingLocationFee}
                        onChange={e => setEditingLocationFee(e.target.value)}
                        onBlur={(e) => submitLocationFeeEdit(e, location)}
                        style={{ width: '90px' }}
                      />
                    </form>
                  ) : (
                    <button
                      type="button"
                      onClick={() => startEditingLocationFee(location)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline dotted' }}
                    >
                      ₦{parseFloat(location.fee).toLocaleString()}
                    </button>
                  )}
                </td>
                <td>
                  <button
                    onClick={() => handleToggleLocationActive(location)}
                    className={location.is_active ? styles.statusAvailable : styles.statusUnavailable}
                  >
                    {location.is_active ? <FaToggleOn /> : <FaToggleOff />}
                    {location.is_active ? "Active" : "Hidden"}
                  </button>
                </td>
                <td className={styles.actions}>
                  <button className={styles.deleteBtn} onClick={() => handleDeleteDeliveryLocation(location.id)}><FaTrash /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <PaginationBar pagination={locationsPagination} noun="delivery areas" />
    </div>
  );

  const renderOrders = () => (
    <div className={styles.tabContent}>
      <div className={styles.gridHeader}>
        <h2>Incoming Orders Queue</h2>
        <span className={styles.filterSummary}>Auto-refreshes every 20s</span>
      </div>

      <div className={styles.menuCategoryTabs} role="tablist" style={{ marginBottom: '14px' }}>
        {ORDER_STATUS_FILTERS.map(({ value, label }) => {
          const count = value === "all"
            ? ordersMatchingSearch.length
            : ordersMatchingSearch.filter(order => order.status === value).length;
          return (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={orderStatusFilter === value}
              className={orderStatusFilter === value ? `${styles.menuCategoryTab} ${styles.menuCategoryTabActive}` : styles.menuCategoryTab}
              onClick={() => setOrderStatusFilter(value)}
            >
              {label} ({count})
            </button>
          );
        })}
      </div>

      <div className={styles.filterRow}>
        <div className={styles.menuSearchBar}>
          <FaSearch className={styles.menuSearchIcon} />
          <input
            type="search"
            placeholder="Search order #, customer, room, dish..."
            value={orderSearch}
            onChange={e => setOrderSearch(e.target.value)}
            className={styles.menuSearchInput}
          />
        </div>
        <FilterSelect
          label="Site"
          value={orderSiteFilter}
          onChange={setOrderSiteFilter}
          options={[{ value: "all", label: "Both sites" }, ...STOREFRONTS.map(({ key, label }) => ({ value: key, label }))]}
        />
        <FilterSelect
          label="Sort"
          value={orderSort}
          onChange={setOrderSort}
          options={[{ value: "oldest", label: "Oldest first" }, { value: "newest", label: "Newest first" }]}
        />
      </div>
      <FilterSummary
        shown={filteredOrders.length}
        total={orders.length}
        noun="active orders"
        onClear={ordersFiltersActive ? () => { setOrderSearch(""); setOrderStatusFilter("all"); setOrderSiteFilter("all"); } : null}
      />

      <div className={styles.orderGrid}>
        {filteredOrders.length === 0 ? (
          <p>{orders.length === 0 ? "No active orders." : "No orders match these filters."}</p>
        ) : ordersPagination.pageItems.map(order => (
          <div key={order.id} className={`${styles.orderCard} ${styles[order.status]}`}>
            <div className={styles.orderHeader}>
              <strong>
                Order #{order.id}
                {order.storefront && (
                  <span className={styles.categoryAddonsBadge} style={{ marginLeft: '8px' }}>
                    {STOREFRONTS.find(s => s.key === order.storefront)?.label || order.storefront}
                  </span>
                )}
              </strong>
              <span className={styles.timeTag} style={{ color: waitingLabel(order.created_at).color }}>
                <FaClock /> {new Date(order.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                {' · '}{waitingLabel(order.created_at).text}
              </span>
            </div>
            <div className={styles.orderDetails}>
              <p><strong>Customer:</strong> {order.customer_name || 'Guest'}</p>
              <p><strong>Location:</strong> {order.location || order.room_number}</p>
              <ul className={styles.itemList}>
                {order.items.map((item, idx) => (
                  <li key={idx}>{item.quantity}x {item.item_name}</li>
                ))}
              </ul>
            </div>
            <div className={styles.orderActions}>
              {order.status === 'pending' && (
                <button onClick={() => handleUpdateStatus(order.id, 'processing')} className={styles.btnProcess}>Start Prep</button>
              )}
              {order.status === 'processing' && (
                <button onClick={() => handleUpdateStatus(order.id, 'prepared')} className={styles.btnReady}>Mark Ready</button>
              )}
              {order.status === 'prepared' && (
                <button onClick={() => handleUpdateStatus(order.id, 'delivered')} className={styles.btnDeliver}>Out for Delivery</button>
              )}
            </div>
          </div>
        ))}
      </div>
      <PaginationBar pagination={ordersPagination} noun="orders" />
    </div>
  );

  const renderChoiceGroups = () => (
    <div className={styles.tabContent}>
      <div className={styles.gridHeader}>
        <h2>Choice Groups</h2>
        <button className={styles.addBtn} onClick={() => openGroupRules(null)}>
          <FaPlus /> New Group
        </button>
      </div>
      <p style={{ fontSize: '13px', color: '#666', marginTop: '-10px' }}>
        The steps a customer picks from — "Choose your soup", or a combo's "Choose your protein" and "Choose your drink".
        Attach groups to a category (Menu Items tab) or to one item (its edit form), where they replace the category's.
      </p>
      <div className={styles.filterRow}>
        <div className={styles.menuSearchBar}>
          <FaSearch className={styles.menuSearchIcon} />
          <input
            type="search"
            placeholder="Search groups or options..."
            value={groupSearch}
            onChange={e => setGroupSearch(e.target.value)}
            className={styles.menuSearchInput}
          />
        </div>
        <FilterSelect
          label="Show"
          value={groupUsageFilter}
          onChange={setGroupUsageFilter}
          options={[
            { value: "all", label: "All groups" },
            { value: "required", label: "Required" },
            { value: "optional", label: "Optional" },
            { value: "unused", label: "Not used anywhere" },
          ]}
        />
      </div>
      <FilterSummary
        shown={filteredGroups.length}
        total={addonGroups.length}
        noun="choice groups"
        onClear={groupSearch || groupUsageFilter !== "all" ? () => { setGroupSearch(""); setGroupUsageFilter("all"); } : null}
      />
      <div className={styles.tableResponsive}>
        <table className={styles.itemTable}>
          <thead>
            <tr>
              <th>Group</th>
              <th>Rules</th>
              <th>Options</th>
              <th>Used by</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredGroups.length === 0 ? (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                {addonGroups.length === 0 ? "No choice groups yet." : "No choice groups match these filters."}
              </td></tr>
            ) : groupsPagination.pageItems.map(group => (
              <tr key={group.id}>
                <td>{group.name}</td>
                <td>{groupRuleSummary(group)}</td>
                <td style={{ maxWidth: '360px' }}>
                  {(group.addons || []).length
                    ? group.addons.map(describeGroupOption).join(", ")
                    : <span style={{ color: '#999' }}>No options yet</span>}
                </td>
                <td>{group.menu_items_count || 0} item(s), {group.categories_count || 0} categor{group.categories_count === 1 ? 'y' : 'ies'}</td>
                <td className={styles.actions}>
                  <button onClick={() => openGroupMembers(group)}>Options</button>
                  <button onClick={() => openGroupRules(group)}><FaEdit /></button>
                  <button className={styles.deleteBtn} onClick={() => handleDeleteGroup(group)}><FaTrash /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <PaginationBar pagination={groupsPagination} noun="choice groups" />
    </div>
  );

  const renderAddons = () => (
    <div className={styles.tabContent}>
      <div className={styles.gridHeader}>
        <h2>Add-ons</h2>
      </div>
      <p style={{ fontSize: '13px', color: '#666', marginTop: '-10px' }}>
        Each site has its own add-on prices and listing. Sold out applies to both sites.
      </p>
      <div className={styles.filterRow}>
        <div className={styles.menuSearchBar}>
          <FaSearch className={styles.menuSearchIcon} />
          <input
            type="search"
            placeholder="Search add-ons..."
            value={addonSearch}
            onChange={e => setAddonSearch(e.target.value)}
            className={styles.menuSearchInput}
          />
        </div>
        <FilterSelect
          label="Type"
          value={addonTypeFilter}
          onChange={setAddonTypeFilter}
          options={[
            { value: "all", label: "All types" },
            ...[...new Set(addons.map(addon => addon.type))].sort().map(type => ({ value: type, label: type.charAt(0).toUpperCase() + type.slice(1) })),
          ]}
        />
        <FilterSelect
          label="Group"
          value={addonGroupFilter}
          onChange={setAddonGroupFilter}
          options={[
            { value: "all", label: "All groups" },
            ...addonGroups.map(group => ({ value: String(group.id), label: group.name })),
            { value: "none", label: "No group" },
          ]}
        />
        <FilterSelect
          label="Stock"
          value={addonStockFilter}
          onChange={setAddonStockFilter}
          options={[
            { value: "all", label: "Any stock" },
            { value: "in_stock", label: "Available" },
            { value: "sold_out", label: "Sold out" },
          ]}
        />
      </div>
      <FilterSummary
        shown={filteredAddons.length}
        total={addons.length}
        noun="add-ons"
        onClear={addonFiltersActive ? () => { setAddonSearch(""); setAddonTypeFilter("all"); setAddonGroupFilter("all"); setAddonStockFilter("all"); } : null}
      />
      <div className={styles.tableResponsive}>
        <table className={styles.itemTable}>
          <thead>
            <tr>
              <th>Add-on</th>
              <th>Group</th>
              {STOREFRONTS.map(({ key, label }) => <th key={key}>{label}</th>)}
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAddons.length === 0 ? (
              <tr><td colSpan={4 + STOREFRONTS.length} style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                {addons.length === 0 ? "No add-ons yet." : "No add-ons match these filters."}
              </td></tr>
            ) : addonsPagination.pageItems.map(addon => (
              <tr key={addon.id}>
                <td>
                  {addon.name}
                  <div style={{ fontSize: '12px', color: '#999', textTransform: 'capitalize' }}>{addon.type}</div>
                </td>
                <td>{addon.group?.name || <span style={{ color: '#999' }}>—</span>}</td>
                {STOREFRONTS.map(({ key }) => {
                  const listed = storefrontRow(addon, key)?.is_listed ?? true;
                  return (
                    <td key={key} style={listed ? undefined : { color: '#999' }}>
                      {formatAddonPrices(addon, key)}
                      {!listed && <div style={{ fontSize: '12px' }}>Hidden</div>}
                    </td>
                  );
                })}
                <td>
                  <button
                    onClick={() => toggleAddonAvailability(addon)}
                    className={addon.is_available ? styles.statusAvailable : styles.statusUnavailable}
                  >
                    {addon.is_available ? <FaToggleOn /> : <FaToggleOff />}
                    {addon.is_available ? "Available" : "Sold Out"}
                  </button>
                </td>
                <td className={styles.actions}>
                  <button onClick={() => openAddonModal(addon)}><FaEdit /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <PaginationBar pagination={addonsPagination} noun="add-ons" />
    </div>
  );

  const renderMenuManagement = () => (
    <div className={styles.tabContent}>
      <div className={styles.gridHeader}>
        <h2>Menu Inventory</h2>
        <div className={styles.headerActions}>
          <div className={styles.addCategoryGroup}>
            <input
              type="text"
              placeholder="New category name"
              value={newCategoryName}
              onChange={e => setNewCategoryName(e.target.value)}
              className={styles.categoryInput}
            />
            <button
              className={styles.addSmallBtn}
              onClick={async () => {
                if (!newCategoryName.trim()) return;
                try {
                  const res = await api.post('/chopnation/kitchen/categories', { name: newCategoryName.trim() });
                  setCategories(prev => [...prev, res.data.category]);
                  setNewCategoryName('');
                } catch (err) {
                  alert(err.response?.data?.message || 'Failed to add category');
                }
              }}
            >Add Category</button>
          </div>
        
        <button className={styles.addBtn} onClick={() => {
            setEditingItem(null);
            setItemForm(emptyItemForm());
            setShowItemModal(true);
        }}>
          <FaPlus /> New Item
        </button>
        </div>
      </div>

      <div className={styles.menuFilters}>
        <div className={styles.menuSearchBar}>
          <FaSearch className={styles.menuSearchIcon} />
          <input
            type="text"
            placeholder="Search menu items..."
            value={menuSearchQuery}
            onChange={e => setMenuSearchQuery(e.target.value)}
            className={styles.menuSearchInput}
          />
        </div>
        <div className={styles.filterRow}>
          <FilterSelect
            label="Shown on"
            value={menuSiteFilter}
            onChange={setMenuSiteFilter}
            options={[
              { value: "all", label: "Any site" },
              ...STOREFRONTS.map(({ key, label }) => ({ value: key, label: `On ${label}` })),
              ...STOREFRONTS.map(({ key, label }) => ({ value: `only_${key}`, label: `Only on ${label}` })),
              { value: "hidden_both", label: "Hidden on both" },
            ]}
          />
          <FilterSelect
            label="Stock"
            value={menuStockFilter}
            onChange={setMenuStockFilter}
            options={[
              { value: "all", label: "Any stock" },
              { value: "in_stock", label: "Available" },
              { value: "sold_out", label: "Sold out" },
            ]}
          />
        </div>
        <div className={styles.menuCategoryTabs}>
          <button
            type="button"
            className={menuCategoryFilter === "all" ? `${styles.menuCategoryTab} ${styles.menuCategoryTabActive}` : styles.menuCategoryTab}
            onClick={() => setMenuCategoryFilter("all")}
          >
            All
          </button>
          {categories.map(cat => (
            <button
              key={cat.id}
              type="button"
              className={String(menuCategoryFilter) === String(cat.id) ? `${styles.menuCategoryTab} ${styles.menuCategoryTabActive}` : styles.menuCategoryTab}
              onClick={() => setMenuCategoryFilter(cat.id)}
            >
              {cat.name}
            </button>
          ))}
        </div>
        <FilterSummary
          shown={filteredMenuItems.length}
          total={menuItems.length}
          noun="menu items"
          onClear={menuFiltersActive ? () => { setMenuSearchQuery(""); setMenuCategoryFilter("all"); setMenuSiteFilter("all"); setMenuStockFilter("all"); } : null}
        />
      </div>

      <div className={styles.gridHeader}>
        <h2>Categories</h2>
        <div className={styles.addCategoryGroup}>
          <label htmlFor="category-order-storefront" style={{ fontSize: '13px', color: '#666' }}>Order for</label>
          <select
            id="category-order-storefront"
            value={categoryOrderStorefront}
            onChange={e => setCategoryOrderStorefront(e.target.value)}
            className={styles.categoryInput}
          >
            {STOREFRONTS.map(({ key, label }) => <option key={key} value={key}>{label}</option>)}
          </select>
        </div>
      </div>
      <div className={styles.tableResponsive}>
        <table className={styles.itemTable}>
          <thead>
            <tr>
              <th>Order</th>
              <th>Category</th>
              <th>Menu Tab</th>
              {STOREFRONTS.map(({ key, label }) => <th key={key}>{label}</th>)}
              <th>Add-on Groups</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {categories.length === 0 ? (
              <tr><td colSpan={5 + STOREFRONTS.length} style={{ textAlign: 'center', padding: '20px', color: '#999' }}>No categories yet.</td></tr>
            ) : categoriesInOrder(categoryOrderStorefront).map((cat, index, ordered) => (
              <tr key={cat.id}>
                <td className={styles.actions}>
                  <button type="button" aria-label={`Move ${cat.name} up`} disabled={index === 0} onClick={() => moveCategory(index, -1)}>↑</button>
                  <button type="button" aria-label={`Move ${cat.name} down`} disabled={index === ordered.length - 1} onClick={() => moveCategory(index, 1)}>↓</button>
                </td>
                <td>{cat.name}</td>
                <td>
                  <select
                    value={cat.group}
                    disabled={categoryBusyId === cat.id}
                    onChange={e => updateCategory(cat, { group: e.target.value })}
                  >
                    {CATEGORY_GROUPS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                  </select>
                </td>
                {STOREFRONTS.map(({ key }) => {
                  const listed = storefrontRow(cat, key)?.is_listed ?? false;
                  return (
                    <td key={key}>
                      <button
                        type="button"
                        disabled={categoryBusyId === cat.id}
                        onClick={() => toggleCategoryListing(cat, key)}
                        className={listed ? styles.statusAvailable : styles.statusUnavailable}
                      >
                        {listed ? <FaToggleOn /> : <FaToggleOff />}
                        {listed ? "On menu" : "Hidden"}
                      </button>
                    </td>
                  );
                })}
                <td>
                  {cat.addonGroups?.length > 0
                    ? cat.addonGroups.map(g => (
                        <span key={g.id} className={styles.categoryAddonsBadge} style={{ marginRight: '6px' }}>{g.name}</span>
                      ))
                    : <span style={{ color: '#999' }}>None — items get no add-ons</span>}
                </td>
                <td className={styles.actions}>
                  <button onClick={() => openCategoryAddonsModal(cat)}><FaEdit /> Add-ons</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className={styles.tableResponsive}>
        <table className={styles.itemTable}>
          <thead>
            <tr>
              <th>Image</th>
              <th>Item Name</th>
              <th>Category</th>
              {STOREFRONTS.map(({ key, label }) => <th key={key}>{label} Price</th>)}
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredMenuItems.length === 0 ? (
              <tr><td colSpan={5 + STOREFRONTS.length} style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                {menuItems.length === 0 ? "No menu items yet." : "No menu items match these filters."}
              </td></tr>
            ) : menuPagination.pageItems.map(item => (
              <tr key={item.id}>
                <td>
                  <img
                    src={getImageUrl(item.image, FALLBACK_ITEM_IMAGE)}
                    alt={item.name}
                    className={styles.itemThumb}
                  />
                </td>
                <td>{item.name}</td>
                <td>{item.category?.name}</td>
                {STOREFRONTS.map(({ key }) => {
                  const row = storefrontRow(item, key);
                  return (
                    <td key={key} style={row?.is_listed === false ? { color: '#999' } : undefined}>
                      ₦{parseFloat(row?.price ?? item.base_price).toLocaleString()}
                      {row?.is_listed === false && <div style={{ fontSize: '12px' }}>Hidden</div>}
                    </td>
                  );
                })}
                <td>
                    <button 
                      onClick={() => toggleItemAvailability(item.id)}
                      className={item.is_available ? styles.statusAvailable : styles.statusUnavailable}
                    >
                      {item.is_available ? <FaToggleOn /> : <FaToggleOff />}
                      {item.is_available ? "Available" : "Sold Out"}
                    </button>
                </td>
                <td className={styles.actions}>
                  <button onClick={() => {
                    setEditingItem(item);
                    setItemForm({
                      name: item.name,
                      category_id: item.category_id,
                      prices: Object.fromEntries(STOREFRONTS.map(({ key }) => [
                        key,
                        String(parseFloat(storefrontRow(item, key)?.price ?? item.base_price)),
                      ])),
                      listed: Object.fromEntries(STOREFRONTS.map(({ key }) => [
                        key,
                        storefrontRow(item, key)?.is_listed ?? true,
                      ])),
                      compareAt: Object.fromEntries(STOREFRONTS.map(({ key }) => {
                        const was = storefrontRow(item, key)?.compare_at_price;
                        return [key, was ? String(parseFloat(was)) : ""];
                      })),
                      groupIds: (item.addon_groups || []).map(g => g.id),
                      description: item.description || "",
                      is_available: item.is_available,
                      ingredients: Array.isArray(item.ingredients) ? item.ingredients.join(", ") : (item.ingredients || ""),
                      image: item.image || ""
                    });
                    setShowItemModal(true);
                  }}><FaEdit /></button>
                  <button className={styles.deleteBtn} onClick={() => handleDeleteItem(item.id)}><FaTrash /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <PaginationBar pagination={menuPagination} noun="menu items" />
    </div>
  );

  return (
    <div className={styles.kitchenContainer}>
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <div className={styles.mainContent}>
        <Header onMenuClick={() => setSidebarOpen(!sidebarOpen)} />
        
        <div className={styles.contentWrapper}>
          {error && (
            <div className={styles.errorBanner}>
              <p>{error}</p>
            </div>
          )}
          
          {loading ? (
            <div className={styles.loadingContainer}>
              <p>Loading kitchen dashboard...</p>
            </div>
          ) : (
            <>
              <div className={styles.pageHeader}>
                <div className={styles.headerTitle}>
                  <h1>Kitchen Management</h1>
                  <p>Real-time order processing and menu control.</p>
                </div>
                <div className={styles.tabNav}>
                  <button 
                    className={activeTab === 'orders' ? styles.activeTab : ''} 
                    onClick={() => setActiveTab('orders')}
                  >
                    <FaClipboardList /> Orders
                  </button>
                  <button
                    className={activeTab === 'menu' ? styles.activeTab : ''}
                    onClick={() => setActiveTab('menu')}
                  >
                    <FaUtensils /> Menu Items
                  </button>
                  <button
                    className={activeTab === 'addons' ? styles.activeTab : ''}
                    onClick={() => setActiveTab('addons')}
                  >
                    <FaPlus /> Add-ons
                  </button>
                  <button
                    className={activeTab === 'choices' ? styles.activeTab : ''}
                    onClick={() => setActiveTab('choices')}
                  >
                    <FaClipboardList /> Choice Groups
                  </button>
                  <button
                    className={activeTab === 'delivery' ? styles.activeTab : ''}
                    onClick={() => setActiveTab('delivery')}
                  >
                    <FaClipboardList /> Delivery Areas
                  </button>
                </div>
              </div>

              {activeTab === 'orders' && renderOrders()}
              {activeTab === 'menu' && renderMenuManagement()}
              {activeTab === 'addons' && renderAddons()}
              {activeTab === 'choices' && renderChoiceGroups()}
              {activeTab === 'delivery' && renderDeliveryAreas()}
            </>
          )}
        </div>
      </div>

      {showItemModal && (
        <div className={styles.modalOverlay}>
            <div className={`${styles.modal} ${styles.modalWide}`}>
                <h3>{editingItem ? "Edit Menu Item" : "Add New Menu Item"}</h3>
                <form onSubmit={handleSaveItem}>
                    <div className={styles.formGroup}>
                        <label>Name</label>
                        <input 
                            type="text" 
                            required 
                            value={itemForm.name}
                            onChange={e => setItemForm({...itemForm, name: e.target.value})}
                        />
                    </div>
                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label>Category</label>
                            <select 
                                required 
                                value={itemForm.category_id}
                                onChange={e => setItemForm({...itemForm, category_id: e.target.value})}
                            >
                                <option value="">Select Category</option>
                                {categories.map(cat => (
                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                ))}
                            </select>
                            {itemForm.category_id && (() => {
                                const cat = categories.find(c => String(c.id) === String(itemForm.category_id));
                                const groups = cat?.addonGroups || [];
                                return (
                                    <p style={{ fontSize: '12px', color: '#666', margin: '6px 0 0 0' }}>
                                        {groups.length > 0
                                            ? `Add-ons for this category: ${groups.map(g => g.name).join(', ')}`
                                            : 'No add-ons configured for this category yet.'}
                                        {' '}Manage this under Categories above.
                                    </p>
                                );
                            })()}
                        </div>
                    </div>
                    <div className={styles.formRow}>
                        {STOREFRONTS.map(({ key, label }) => (
                            <div key={key} className={styles.formGroup}>
                                <label>{label} Price (₦)</label>
                                <input
                                    type="number"
                                    min="0"
                                    required
                                    value={itemForm.prices[key]}
                                    onChange={e => setItemForm({ ...itemForm, prices: { ...itemForm.prices, [key]: e.target.value } })}
                                />
                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 'normal', marginTop: '8px' }}>
                                    <input
                                        type="checkbox"
                                        checked={itemForm.listed[key]}
                                        onChange={e => setItemForm({ ...itemForm, listed: { ...itemForm.listed, [key]: e.target.checked } })}
                                    />
                                    Show on {label}
                                </label>
                                <span style={{ fontSize: '12px', color: '#666', marginTop: '8px', display: 'block' }}>"Was" price (optional)</span>
                                <input
                                    type="number"
                                    min="0"
                                    placeholder="Shown struck through"
                                    value={itemForm.compareAt[key]}
                                    onChange={e => setItemForm({ ...itemForm, compareAt: { ...itemForm.compareAt, [key]: e.target.value } })}
                                />
                            </div>
                        ))}
                    </div>
                    <div className={styles.formGroup}>
                        <label>Choice Groups for this Item</label>
                        <p style={{ fontSize: '12px', color: '#666', margin: '0 0 6px 0' }}>
                            Leave empty to use the category's add-on groups. For a combo, tick its steps in the order customers should see them.
                        </p>
                        {addonGroups.length === 0 ? (
                            <p style={{ fontSize: '12px', color: '#999' }}>No choice groups yet — create them in the Choice Groups tab.</p>
                        ) : (
                            <div className={styles.addonGroupList}>
                                {addonGroups.map(group => {
                                    const position = itemForm.groupIds.indexOf(group.id);
                                    return (
                                        <label key={group.id} className={styles.addonGroupOption}>
                                            <input
                                                type="checkbox"
                                                checked={position >= 0}
                                                onChange={() => setItemForm({
                                                    ...itemForm,
                                                    groupIds: position >= 0
                                                        ? itemForm.groupIds.filter(id => id !== group.id)
                                                        : [...itemForm.groupIds, group.id],
                                                })}
                                            />
                                            <span>
                                                {group.name}
                                                {position >= 0 && <span className={styles.addonGroupMeta}> #{position + 1}</span>}
                                            </span>
                                        </label>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    <div className={styles.formGroup}>
                        <label>Description</label>
                        <textarea 
                            value={itemForm.description}
                            onChange={e => setItemForm({...itemForm, description: e.target.value})}
                        />
                    </div>
                    <div className={styles.formGroup}>
                        <label>Ingredients (comma separated)</label>
                        <input 
                            type="text" 
                            placeholder="e.g., Rice, Beans, Beef, Pepper"
                            value={itemForm.ingredients}
                            onChange={e => setItemForm({...itemForm, ingredients: e.target.value})}
                        />
                    </div>
                    <div className={styles.formGroup}>
                        <label>Food Image</label>
                        <input 
                            type="file" 
                            accept="image/*"
                            onChange={e => {
                                setPickedPhoto(e.target.files[0] || null);
                                // Clear it, so picking the same file again still opens the cropper.
                                e.target.value = "";
                            }}
                        />
                        {itemForm.image && typeof itemForm.image === 'string' && (
                            <div className={styles.currentImagePreview}>
                                <img src={getImageUrl(itemForm.image, FALLBACK_ITEM_IMAGE)} alt="Preview" style={{ width: '80px', marginTop: '10px', borderRadius: '4px' }} />
                                <p style={{ fontSize: '12px', color: '#666', margin: '4px 0 0 0' }}>Current Image</p>
                            </div>
                        )}
                        {itemForm.image && itemForm.image instanceof File && (
                            <div className={styles.currentImagePreview}>
                                <img src={URL.createObjectURL(itemForm.image)} alt="Preview" style={{ width: '80px', marginTop: '10px', borderRadius: '4px' }} />
                                <p style={{ fontSize: '12px', color: '#666', margin: '4px 0 0 0' }}>New Image Selected · {formatFileSize(itemForm.image.size)}</p>
                            </div>
                        )}
                    </div>
                    <div className={styles.modalActions}>
                        <button type="button" onClick={() => setShowItemModal(false)} className={styles.cancelBtn}>Cancel</button>
                        <button type="submit" className={styles.submitBtn}>Save Item</button>
                    </div>
                </form>
            </div>
        </div>
      )}

      {pickedPhoto && (
        <ImageCropModal
          file={pickedPhoto}
          onCancel={() => setPickedPhoto(null)}
          onDone={(cropped) => {
            setItemForm(prev => ({ ...prev, image: cropped }));
            setPickedPhoto(null);
          }}
        />
      )}

      {groupRulesTarget && (
        <div className={styles.modalOverlay}>
            <div className={styles.modal}>
                <h3>{groupRulesTarget.id ? `Edit Group — ${groupRulesTarget.name}` : "New Choice Group"}</h3>
                <form onSubmit={handleSaveGroupRules}>
                    <div className={styles.formGroup}>
                        <label>Name</label>
                        <input
                            type="text"
                            required
                            placeholder="Choose your protein"
                            value={groupForm.name}
                            onChange={e => setGroupForm({ ...groupForm, name: e.target.value })}
                        />
                    </div>
                    <div className={styles.formGroup}>
                        <label>Customers can pick</label>
                        <select
                            value={groupForm.selection_type}
                            onChange={e => setGroupForm({ ...groupForm, selection_type: e.target.value })}
                        >
                            <option value="single">One option</option>
                            <option value="multiple">Several options</option>
                        </select>
                    </div>
                    {groupForm.selection_type === "multiple" && (
                        <div className={styles.formRow}>
                            <div className={styles.formGroup}>
                                <label>At least</label>
                                <input type="number" min="0" value={groupForm.min_select} onChange={e => setGroupForm({ ...groupForm, min_select: e.target.value })} />
                            </div>
                            <div className={styles.formGroup}>
                                <label>At most (blank = no limit)</label>
                                <input type="number" min="1" value={groupForm.max_select} onChange={e => setGroupForm({ ...groupForm, max_select: e.target.value })} />
                            </div>
                        </div>
                    )}
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '16px' }}>
                        <input
                            type="checkbox"
                            checked={groupForm.is_required}
                            onChange={e => setGroupForm({ ...groupForm, is_required: e.target.checked })}
                        />
                        Required — customers must choose before ordering
                    </label>
                    <div className={styles.modalActions}>
                        <button type="button" onClick={() => setGroupRulesTarget(null)} className={styles.cancelBtn}>Cancel</button>
                        <button type="submit" className={styles.submitBtn} disabled={savingGroup}>
                            {savingGroup ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
      )}

      {groupMembersEdit && (
        <div className={styles.modalOverlay}>
            <div className={`${styles.modal} ${styles.modalWide}`}>
                <h3>Options — {groupMembersEdit.group.name}</h3>
                <p style={{ fontSize: '13px', color: '#666', marginTop: '-10px', marginBottom: '16px' }}>
                    Tick the add-ons this group offers. "Included" is free with the item (a combo's protein); "Upcharge" is a fixed extra per site; "Normal price" charges the add-on's own price.
                </p>
                <div className={styles.addonGroupList}>
                    {orderedGroupAddons().map(addon => {
                        const member = groupMembersEdit.members[addon.id];
                        return (
                            <div key={addon.id} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px', padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
                                <label className={styles.addonGroupOption} style={{ flex: '1 1 200px', margin: 0 }}>
                                    <input
                                        type="checkbox"
                                        checked={member.included}
                                        onChange={e => updateMember(addon.id, { included: e.target.checked })}
                                    />
                                    <span>
                                        {addon.name}{' '}
                                        <span className={styles.addonGroupMeta}>({addon.type})</span>
                                    </span>
                                </label>
                                {member.included && (
                                    <>
                                        <select value={member.mode} onChange={e => updateMember(addon.id, { mode: e.target.value })}>
                                            {Object.entries(PRICE_MODE_LABELS).map(([mode, label]) => (
                                                <option key={mode} value={mode}>{label}</option>
                                            ))}
                                        </select>
                                        {member.mode === "upcharge" && STOREFRONTS.map(({ key, label }) => (
                                            <input
                                                key={key}
                                                type="number"
                                                min="0"
                                                placeholder={`${label} +₦`}
                                                aria-label={`${label} upcharge for ${addon.name}`}
                                                value={member.upcharges[key]}
                                                onChange={e => updateMember(addon.id, { upcharges: { ...member.upcharges, [key]: e.target.value } })}
                                                style={{ width: '120px' }}
                                            />
                                        ))}
                                    </>
                                )}
                            </div>
                        );
                    })}
                </div>
                <div className={styles.modalActions}>
                    <button type="button" onClick={() => setGroupMembersEdit(null)} className={styles.cancelBtn}>Cancel</button>
                    <button type="button" onClick={handleSaveGroupMembers} className={styles.submitBtn} disabled={savingGroup}>
                        {savingGroup ? 'Saving...' : 'Save Options'}
                    </button>
                </div>
            </div>
        </div>
      )}

      {editingAddon && addonForm && (
        <div className={styles.modalOverlay}>
            <div className={`${styles.modal} ${styles.modalWide}`}>
                <h3>Edit Add-on — {editingAddon.name}</h3>
                <form onSubmit={handleSaveAddon}>
                    <div className={styles.formRow}>
                        {STOREFRONTS.map(({ key, label }) => (
                            <div key={key} className={styles.formGroup}>
                                <label>{label} Price (₦)</label>
                                <input
                                    type="number"
                                    min="0"
                                    required
                                    value={addonForm[key].price}
                                    onChange={e => setAddonField(key, 'price', e.target.value)}
                                />
                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 'normal', marginTop: '8px' }}>
                                    <input
                                        type="checkbox"
                                        checked={addonForm[key].is_listed}
                                        onChange={e => setAddonField(key, 'is_listed', e.target.checked)}
                                    />
                                    Show on {label}
                                </label>
                            </div>
                        ))}
                    </div>
                    <div className={styles.modalActions}>
                        <button type="button" onClick={closeAddonModal} className={styles.cancelBtn}>Cancel</button>
                        <button type="submit" className={styles.submitBtn} disabled={savingAddon}>
                            {savingAddon ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
      )}

      {showCategoryAddonsModal && editingCategoryForAddons && (
        <div className={styles.modalOverlay}>
            <div className={styles.modal}>
                <h3>Add-on Groups — {editingCategoryForAddons.name}</h3>
                <p style={{ fontSize: '13px', color: '#666', marginTop: '-10px', marginBottom: '20px' }}>
                    Every item in this category will offer these add-on groups (e.g. Protein Add-ons, Soup, Spice Level) to customers. There's no per-item override — it's set at the category level.
                </p>
                <form onSubmit={handleSaveCategoryAddonGroups}>
                    <div className={styles.formGroup}>
                        {addonGroups.length === 0 ? (
                            <p style={{ color: '#999' }}>No add-on groups exist yet.</p>
                        ) : (
                            <div className={styles.addonGroupList}>
                                {addonGroups.map(group => (
                                    <label key={group.id} className={styles.addonGroupOption}>
                                        <input
                                            type="checkbox"
                                            checked={selectedAddonGroupIds.includes(group.id)}
                                            onChange={() => toggleSelectedAddonGroup(group.id)}
                                        />
                                        <span>
                                            {group.name}
                                            {' '}
                                            <span className={styles.addonGroupMeta}>
                                                ({group.selection_type}{group.is_required ? ', required' : ''}, {group.addons_count} option{group.addons_count === 1 ? '' : 's'})
                                            </span>
                                        </span>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className={styles.modalActions}>
                        <button
                            type="button"
                            onClick={() => { setShowCategoryAddonsModal(false); setEditingCategoryForAddons(null); }}
                            className={styles.cancelBtn}
                        >Cancel</button>
                        <button type="submit" className={styles.submitBtn} disabled={savingCategoryAddons}>
                            {savingCategoryAddons ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
      )}
    </div>
  );
};

export default KitchenDashboard;
